import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux';
import { getCurrencyDefinitions } from '../../selectors/settings-selectors';
import { getActiveCurrency, getLoggedIn } from '../../selectors/user-selectors';
import { Redirect } from 'react-router';
import { Status } from '../../journey/UserJourneyReducer';
import { getContainer } from '../../ioc/IOCSetup';
import { GenericUnPagedListResponse, PaymentMediumProjection, PaymentStatus, PaymentType, UpdatePaymentResponse, UpdateResult } from '../../http/protocol';
import { t } from '../../i18n';
import moment from 'moment';
import './styles/Transaction.scss';
import { Big } from "big.js";
import { getAmountClass, getStatus, getTypeTranslation } from '../TransactionUtils';
import TransactionInformationModal from './TransactionInformationModal';
import { Header, Table, TableRow } from '../../core/ui/organisms/Table';
import { usePrevious } from '../../user/ui/utils';
import { InfoIcon, MemoizedTransactionCellAmountComponent, MemoizedTransactionCellReferenceComponent, MemoizedTransactionCellTimeStampComponent, MemoizedTransactionCellTransactioComponent, MemoizedTransactionCellTypeComponent, MemoizedTransactionColumnCancelComponent, MemoizedTransactionColumnTransactionIdComponent, TableStatusAndPaginations } from './TransactionRowComponents';
import WithdrawCancellation from './WithdrawCancellation';


type TransactionViewModel = {
    type: { type: PaymentType, translations: string, class: string };
    status: { name: string, classname: string, transactionInformation: () => void };
    cancel: { cancelPayment: () => void, hasAction: boolean };
    timeStamp: string;
    reference: string | number;
    transactionId?: { transactionId: number | string; referenceUrl: string, action: () => void, status: PaymentStatus | string };
    amount: { amount: number | string; currency: string; status: PaymentStatus | string };
};


const transactionStatusRequest = [
    PaymentStatus.COMPLETED,
    PaymentStatus.CANCELED,
    PaymentStatus.FAILED,
    PaymentStatus.FAILED_PERMANENTLY,
    PaymentStatus.PENDING,
    PaymentStatus.RESOLVED,
    PaymentStatus.INITIATED,
];


/**
 * 
 * @Todo - Fix translation for status labels. 
 */
export default function WalletTransactions() {
    const userLoggedIn = useSelector(getLoggedIn);
    const activeCurrency = useSelector(getActiveCurrency);
    const previousCurrency = usePrevious(activeCurrency);
    const currencyDefinitions = useSelector(getCurrencyDefinitions)
    const [proceedCancellation, setProceedCancellation] = useState<{ proceed: boolean, paymentId: number } | undefined>(undefined)
    const [showTransactionInformation, setShowTransactionInformation] = useState<{ type: 'status' | 'transactionId' | undefined, active: boolean }>({ type: undefined, active: false });
    const toggleTransactionInformationModal = (type: 'status' | 'transactionId' | undefined) => {
        setShowTransactionInformation((p) => ({ type: type, active: !p.active }))
    }


    const [asyncStatus, setAsyncStatus] = useState<Status>(Status.NONE)
    const [pagination, setPagination] = useState({ offset: 0, limit: 20 });

    const [paymentTransactions, setPaymentTransactions] = useState<GenericUnPagedListResponse<PaymentMediumProjection>>({ items: undefined })


    const findCurrencyDefinition = (code: string) => currencyDefinitions.find((c) => c.code == code)
    const formatByFractionalDigits = (amount: any, code: string) => {
        let fractionalDigits = findCurrencyDefinition(code)?.fractionalDigits;
        return new Big(amount).toFixed(fractionalDigits)
    }



    const handlePagination = () => {
        setPagination((prev) => ({ ...prev, offset: prev.offset + prev.limit }));
    };


    const getTransactions = useCallback(async () => {
        setAsyncStatus(Status.LOADING);
        try {

            const response = await new Promise<GenericUnPagedListResponse<PaymentMediumProjection>>((resolve, reject) => {
                getContainer()
                    .getTransactionService()
                    .listPaymentsTransactions({ listRequest: pagination, statuses: transactionStatusRequest, currency: activeCurrency?.code })
                    .then(resolve)
                    .catch(reject);
            })

            if (pagination.offset === 0) {
                setPaymentTransactions({ items: response.items ?? [], paged: response.paged });
            } else {
                setPaymentTransactions((p) => ({ items: [...p.items ?? [], ...response.items ?? []], paged: response.paged }));
            }
            setAsyncStatus(Status.DONE);
        } catch (err) {
            setAsyncStatus(Status.ERROR);
        }
    }, [pagination, activeCurrency]);

    // Callback function to refresh transactions after successful cancellation
    const onCancellationSuccess = useCallback(() => {
        // Reset pagination to start
        setPagination({ offset: 0, limit: 20 });
        // Fetch transactions with the updated data
        getTransactions();
    }, [getTransactions]);

    useEffect(() => {
        if (paymentTransactions.items == undefined || activeCurrency !== previousCurrency) {
            getTransactions();
        }
        return () => {
            setPagination({ offset: 0, limit: 20 });
            setPaymentTransactions({ items: undefined, paged: undefined });
        }
    }, [activeCurrency])

    useEffect(() => {
        if (pagination.offset !== 0) {
            getTransactions();
        }
    }, [pagination]);


    const generateReferenceUrl = (cryptoTxReference: string, currency: string) => {
        const explorerUrls = {
            BTC: "https://live.blockcypher.com/btc/tx/",
            mBTC: "https://live.blockcypher.com/btc/tx/",
            uBTC: "https://live.blockcypher.com/btc/tx/",
            SAT: "https://live.blockcypher.com/btc/tx/",
            DOGE: "https://live.blockcypher.com/doge/tx/",
            ETH: "https://etherscan.io/tx/",
            mETH: "https://etherscan.io/tx/",
            LTC: "https://live.blockcypher.com/ltc/tx/",
            mLTC: "https://live.blockcypher.com/ltc/tx/",
            SOL: "https://explorer.solana.com/tx/",
            TRX: "https://tronscan.org/#/transaction/",
            BUSD: "https://bscscan.com/tx/",
            BNB: "https://bscscan.com/tx/"
        } as const;

        return explorerUrls[currency as keyof typeof explorerUrls] ? `${explorerUrls[currency as keyof typeof explorerUrls]}${cryptoTxReference}` : "";
    };



    const requestCancellation = async (id: number) => {
        setProceedCancellation({ paymentId: id, proceed: true })
    }

    const closeCancellationModal = () => setProceedCancellation(undefined)

    const handleTransactionClick = (value: any) => {
        console.log(value);
    }

    const hasTransactions = paymentTransactions.items !== undefined && paymentTransactions.items?.length > 0;


    const isCryptoCurrency = activeCurrency?.cryptoCurrency;

    const mapTransactionToViewModel = useCallback(
        (transaction: PaymentMediumProjection): TransactionViewModel => {
            const {
                type = PaymentType.DEPOSIT,
                created = new Date(),
                status = PaymentStatus.PLACEHOLDER,
                paymentTxId = 0,
                id = 0,
                cryptoTxReference = "",
                amount = 0,
                isCancellable = false,
                currency = "USD"
            } = transaction || {};

            const referenceUrl = generateReferenceUrl(cryptoTxReference, currency);
            const formattedTimeStamp = moment(created).format("MMM DD, YYYY, h:mm A");
            const formattedStatus = getStatus(status);
            const formattedAmount = formatByFractionalDigits(amount, currency);
            const isPositiveAmountClass = getAmountClass(Number(formattedAmount));

            const transactionViewModel: TransactionViewModel = {
                type: { type, translations: getTypeTranslation(type) as any, class: isPositiveAmountClass },
                cancel: { cancelPayment: () => requestCancellation(id), hasAction: false }, // Disabled for now, if enabled use isCancellable
                timeStamp: formattedTimeStamp,
                status: { name: formattedStatus.translation, classname: formattedStatus.classname, transactionInformation: toggleTransactionInformationModal as any },
                reference: id,
                amount: {
                    amount: type === PaymentType.WITHDRAWAL ? `-${formattedAmount}` : formattedAmount,
                    currency,
                    status
                },
            };

            if (isCryptoCurrency) {
                transactionViewModel.transactionId = {
                    action: () => handleTransactionClick(cryptoTxReference),
                    referenceUrl,
                    transactionId: cryptoTxReference,
                    status,
                };
            }

            return transactionViewModel;
        },
        [requestCancellation, handleTransactionClick, generateReferenceUrl, isCryptoCurrency]
    );


    const transactionColumnLabels: Record<string, Header> = {
        'type': { name: t('transactions.transaction_wallet_table.type') },
        'status': { name: t('transactions.transaction_wallet_table.status'), component: <InfoIcon variant="questionMark" onClick={() => toggleTransactionInformationModal('status')} /> },
        'reference': { name: t('transactions.transaction_wallet_table.reference') },
        'transactionId': { name: t('transactions.transaction_wallet_table.transaction_id'), component: <InfoIcon variant="questionMark" onClick={() => toggleTransactionInformationModal('transactionId')} /> },
        'amount': { name: t('transactions.transaction_wallet_table.amount') }
    };

    const columnComponents = {
        'type': (key: string, value: any) => <MemoizedTransactionCellTypeComponent key={key} value={value} />,
        'cancel': (key: string, value: any) => <MemoizedTransactionColumnCancelComponent key={key} value={value} onClick={value.cancelPayment} />,
        'timeStamp': (key: string, value: any) => <MemoizedTransactionCellTimeStampComponent key={key} value={value} />,
        'status': (key: string, value: any) => <MemoizedTransactionCellTransactioComponent key={key} value={value} onClick={() => value.transactionInformation('status')} />,
        'reference': (key: string, value: any) => <MemoizedTransactionCellReferenceComponent key={key} value={value} />,
        'transactionId': (key: string, value: any) => <MemoizedTransactionColumnTransactionIdComponent key={key} value={value} onClick={value.action} />,
        'amount': (key: string, value: any) => <MemoizedTransactionCellAmountComponent key={key} value={value} />,
    }

    const filtredTransactionColumnLabels: Record<string, Header> = useMemo(() => {
        return isCryptoCurrency ? transactionColumnLabels : Object.fromEntries(
            Object.entries(transactionColumnLabels).filter(([key]) => key !== 'transactionId')
        );
    }, [t, isCryptoCurrency]);


    const filtredColumnComponents = useMemo(() => {
        return isCryptoCurrency ? columnComponents : Object.fromEntries(
            Object.entries(columnComponents).filter(([key]) => key !== 'transactionId')
        );
    }, [t, isCryptoCurrency]);


    if (!userLoggedIn) {
        return <Redirect to={"/"} />
    }


    return (
        <>
            <Table headers={filtredTransactionColumnLabels} className={`wallet-transaction-table ${isCryptoCurrency ? 'isCrypto' : 'isFiat'}`}>
                {paymentTransactions.items?.map((data: PaymentMediumProjection, i: React.Key) => {
                    return (
                        <TableRow
                            key={data.id}
                            columnComponents={filtredColumnComponents as any}
                            columnData={mapTransactionToViewModel(data)}
                            className="wallet-transaction"
                        />
                    )
                })}
                <TableStatusAndPaginations status={asyncStatus} handlePagination={handlePagination} hasTransactions={hasTransactions} activeCurrency={{ name: activeCurrency?.name, code: activeCurrency?.code }} nrOfTransactions={paymentTransactions.items?.length} />
            </Table>
            {showTransactionInformation.active && <TransactionInformationModal show={showTransactionInformation.active} type={showTransactionInformation.type} onHide={toggleTransactionInformationModal} />}
            <WithdrawCancellation
                paymentId={proceedCancellation?.paymentId}
                isOpen={proceedCancellation?.proceed}
                onHide={closeCancellationModal}
                onSuccess={onCancellationSuccess}
            />
        </>
    )
}







